import { useEffect, useState, useCallback } from "react";
import { Icon, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtDate } from "../../lib/dates.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import {
  listChangelog,
  createChangelog,
  updateChangelog,
  deleteChangelog,
} from "../../lib/adminApi";

const CATEGORIES = [
  { key: "feature", label: "New feature", badge: "badge-primary" },
  { key: "improvement", label: "Improvement", badge: "badge-info" },
  { key: "fix", label: "Fix", badge: "badge-success" },
  { key: "announcement", label: "Announcement", badge: "badge-warning" },
];
const catMeta = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[0];

function ChangelogInner() {
  const { toast, openModal, closeModal } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listChangelog();
      setItems(d?.items || []);
    } catch (e) {
      toast(e.message || "Failed to load changelog", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openForm = (entry) => {
    const draft = {
      title: entry?.title || "",
      version: entry?.version || "",
      category: entry?.category || "feature",
      body: entry?.body || "",
      published: entry ? entry.published : true,
    };
    const set = (k, v) => { draft[k] = v; };

    const save = async () => {
      if (!draft.title.trim() || !draft.body.trim()) {
        toast("Title and body are required", "error");
        return;
      }
      try {
        if (entry) await updateChangelog(entry.id, draft);
        else await createChangelog(draft);
        toast(entry ? "Update saved" : "Update published", "success");
        closeModal();
        load();
      } catch (e) {
        toast(e.message || "Save failed", "error");
      }
    };

    openModal(
      <>
        <div className="modal-head">
          <h3>{entry ? "Edit update" : "New update"}</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <div className="field">
            <label>Title *</label>
            <input className="input" defaultValue={draft.title} maxLength={191}
              onChange={(e) => set("title", e.target.value)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field">
              <label>Version (optional)</label>
              <input className="input" defaultValue={draft.version} placeholder="v2.4.0"
                onChange={(e) => set("version", e.target.value)} />
            </div>
            <div className="field">
              <label>Category</label>
              <select className="input" defaultValue={draft.category}
                onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label>Body *</label>
            <textarea className="input" rows={6} defaultValue={draft.body}
              placeholder="Describe what changed…"
              onChange={(e) => set("body", e.target.value)} style={{ resize: "vertical" }} />
          </div>
          <label className="checkbox" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" defaultChecked={draft.published}
              onChange={(e) => set("published", e.target.checked)} />
            Published (visible to users)
          </label>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>
            <Icon name="check" size={14} /> {entry ? "Save" : "Publish"}
          </button>
        </div>
      </>,
      "lg",
    );
  };

  const confirmDelete = (entry) => {
    openModal(
      <>
        <div className="modal-head">
          <h3>Delete update</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 14, color: "var(--text-muted)" }}>
            Delete <strong style={{ color: "var(--text)" }}>{entry.title}</strong>? This cannot be undone.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-danger" onClick={async () => {
            try {
              await deleteChangelog(entry.id);
              toast("Update deleted", "success");
              closeModal();
              load();
            } catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="trash-2" size={14} /> Delete
          </button>
        </div>
      </>,
      "sm",
    );
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Latest Updates</h1>
          <p className="page-sub">{items.length} changelog {items.length === 1 ? "entry" : "entries"}</p>
        </div>
        <button className="btn btn-primary" onClick={() => openForm(null)}>
          <Icon name="plus" size={14} /> New update
        </button>
      </div>

      {loading ? (
        <TableSkeleton columns={5} rows={5} />
      ) : items.length === 0 ? (
        <EmptyState title="No updates yet" desc="Publish your first product update." icon="sparkles" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Version</th>
                <th>Status</th>
                <th>Published</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const cm = catMeta(it.category);
                return (
                  <tr key={it.id}>
                    <td data-label="Title" style={{ fontWeight: 600, fontSize: 13, maxWidth: 320 }}>
                      {it.title}
                    </td>
                    <td data-label="Category">
                      <span className={`badge ${cm.badge}`}>{cm.label}</span>
                    </td>
                    <td data-label="Version" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {it.version || "—"}
                    </td>
                    <td data-label="Status">
                      <span className={`badge ${it.published ? "badge-success" : "badge-muted"}`}>
                        <span className="badge-dot" /> {it.published ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td data-label="Published" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {fmtDate(it.publishedAt)}
                    </td>
                    <td data-label="">
                      <button className="icon-btn" title="Edit" onClick={() => openForm(it)}>
                        <Icon name="pencil" size={14} />
                      </button>
                      <button className="icon-btn" title="Delete" onClick={() => confirmDelete(it)}>
                        <Icon name="trash-2" size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminChangelog() {
  return (
    <AdminPageBoundary>
      <ChangelogInner />
    </AdminPageBoundary>
  );
}
